import React from 'react';
import { BookOpen, CalendarDays, CircleUserRound, GraduationCap, Mail, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { studentService } from '../../../services/studentService';
import * as enrollmentService from '../../../services/enrollmentService';
import StudentLayout from '../../../components/layouts/StudentLayout';

const value = (input, fallback = 'Not provided') => input == null || input === '' ? fallback : input;
const formatDate = input => input ? new Intl.DateTimeFormat('en-KE', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(input)) : 'Not provided';

const StudentDashboard = () => {
  const { user, profile } = useAuth();
  const [student, setStudent] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let mounted = true;
    const loadStudent = async () => {
      if (!user?.id) return;
      try {
        const data = await studentService.getStudentById(user.id);
        if (!data) {
          if (mounted) setStudent(null);
          return;
        }

        // Recalculate the module before rendering the dashboard. Result
        // release triggers cover the normal workflow, while this read repairs
        // students whose results were released before the progression trigger
        // was installed or whose dashboard stayed open during release.
        let progression = null;
        try {
          progression = await enrollmentService.syncStudentModuleProgression(user.id);
        } catch (error) {
          console.warn('Student progression refresh was skipped:', error?.message || error);
        }
        const resolvedModule = Math.max(
          Number(progression?.data?.current_module || 0),
          Number(data.current_module || data.starting_module || 1),
          1,
        );
        if (mounted) setStudent({ ...data, current_module: resolvedModule });
      } catch (error) {
        console.warn('Student record could not be loaded:', error?.message || error);
        if (mounted) setStudent(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadStudent();
    return () => { mounted = false; };
  }, [user?.id]);

  if (loading) return <StudentLayout><div className="grid min-h-[45vh] place-items-center text-slate-500">Loading your student record…</div></StudentLayout>;
  if (!student) return <StudentLayout><div className="mx-auto max-w-xl rounded-xl border border-amber-200 bg-amber-50 p-8 text-center"><h1 className="text-xl font-semibold">Student record unavailable</h1><p className="mt-2 text-sm text-slate-600">Your login is valid, but it is not linked to a student record. Contact the registrar and quote {profile?.email || user?.email}.</p></div></StudentLayout>;

  const fullName = [student.first_name, student.middle_name, student.last_name].filter(Boolean).join(' ');
  const programmeLevel = student.academic_level
    ? (/^level\b/i.test(String(student.academic_level).trim()) ? String(student.academic_level).trim() : `Level ${student.academic_level}`)
    : 'Not assigned';
  const summary = [
    { label: 'Account status', value: value(student.status || student.enrollment_status), icon: ShieldCheck },
    { label: 'Programme level', value: programmeLevel, icon: GraduationCap },
    { label: 'Current module', value: student.current_module ? `Module ${student.current_module}` : 'Not assigned', icon: BookOpen }
  ];
  const details = [
    ['Registration number', student.admission_number], ['ID / Passport', student.id_number], ['Full name', fullName],
    ['Gender', student.gender], ['Date of birth', formatDate(student.date_of_birth)], ['Phone number', student.phone_number],
    ['Student email', student.institutional_email || student.email], ['Backup email', student.personal_email],
    ['Programme', student.program ? `${student.program.code || ''} — ${student.program.name}` : null], ['Intake', student.intake_code],
    ['Starting module', student.starting_module ? `Module ${student.starting_module}` : null], ['Academic standing', student.academic_standing]
  ];

  return <StudentLayout student={student}>
    <>
    <section className="mb-6 overflow-hidden rounded-2xl bg-primary text-primary-foreground shadow-lg">
      <div className="grid gap-6 p-6 xl:grid-cols-[minmax(300px,0.9fr)_minmax(620px,1.6fr)] xl:items-center">
        <div><p className="text-sm text-primary-foreground/75">Welcome back</p><h1 className="mt-1 text-2xl font-bold md:text-3xl">{fullName}</h1><p className="mt-2 text-sm text-primary-foreground/70">{student.admission_number} · {student.program?.name || 'Programme not assigned'}</p></div>
        <div className="grid gap-3 sm:grid-cols-3">{summary.map(item => <div key={item.label} className="flex items-center gap-3 rounded-xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm"><span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-white/10 text-primary-foreground/75"><item.icon className="h-5 w-5"/></span><div><p className="text-[0.6875rem] font-semibold uppercase tracking-wide text-primary-foreground/75">{item.label}</p><p className="mt-1 text-base font-bold capitalize text-primary-foreground">{item.value}</p></div></div>)}</div>
      </div>
    </section>
    <section className="mt-6 grid gap-6 lg:grid-cols-[320px_1fr]">
      <aside className="rounded-xl border bg-white p-6 text-center shadow-sm"><span className="mx-auto grid h-32 w-32 place-items-center rounded-full bg-blue-100 text-blue-700"><CircleUserRound className="h-20 w-20"/></span><h2 className="mt-5 text-xl font-bold">{fullName}</h2><p className="mt-1 font-mono text-sm text-slate-500">{student.admission_number}</p><div className="mt-6 border-t pt-5 text-left"><p className="text-xs font-semibold uppercase text-slate-500">Programme</p><p className="mt-2 text-sm font-medium">{student.program?.name || 'Not assigned'}</p><p className="mt-1 text-xs text-slate-500">{student.program?.code || ''}</p></div></aside>
      <div className="rounded-xl border bg-white p-6 shadow-sm"><div className="flex items-center justify-between border-b pb-4"><div><h2 className="text-xl font-bold">Student information</h2><p className="mt-1 text-sm text-slate-500">Official profile and academic placement</p></div><Mail className="h-5 w-5 text-slate-400"/></div><dl className="mt-5 grid gap-x-10 md:grid-cols-2">{details.map(([label, itemValue]) => <div key={label} className="border-b py-3"><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt><dd className="mt-1 break-words text-sm font-medium text-slate-900">{value(itemValue)}</dd></div>)}</dl></div>
    </section>
    </>
  </StudentLayout>;
};
export default StudentDashboard;
